import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from '@remix-run/react';
import { pxArrayToRem, SearchableDropdown } from '@archipro-design/aria';
import { CustomLocation, SearchLine } from '@archipro-design/icons';
import { useCountryContext } from '@archipro-website/localisation';
import { FindPlaceApiProviderEnum, FindPlaceTypeEnum } from 'generated/graphql';
import type { DropdownItem } from '~/modules/enquiry/types';
import type { BasicPlace } from '~/modules/location/type';
import type { IUseCurrentLocationResult } from '~/modules/location/hook/use-current-location';
import type { SearchLocation } from '../../type/api';
import type { FilterStatesKey } from '../../type/filter-state';
import {
    formatLocationSearchFilterText,
    MIN_PLACE_FINDER_KEYWORD_LENGTH,
    usePlaceFinder,
} from '~/modules/location/hook/use-place-finder';
import { getRegionNameByCountry } from '~/modules/location/util';

export interface LocationFilter {
    kind: 'Location';
    filterKey: FilterStatesKey;
    subFilterKey?: string;
}

export interface LocationFilterState extends Pick<LocationFilter, 'kind'> {
    selectedLocation?: SearchLocation;
}

interface FilterLocationProps extends IUseCurrentLocationResult {
    filter: LocationFilter;
    state: LocationFilterState;
    updateFilter: (
        filterKey: FilterStatesKey,
        state: Partial<LocationFilterState>,
        updateStates?: boolean,
    ) => void;
}

/**
 * Shimmer skeleton rows shown in the suggestions dropdown while location
 * results are loading. Mimics the shape of a suggestion (name + place type).
 */
const LocationSuggestionsSkeleton = () => (
    <div aria-hidden className="flex flex-col gap-3 px-2 py-1">
        {[0, 1, 2].map((row) => (
            <div key={row} className="flex animate-pulse flex-col gap-1.5">
                <div className="h-3.5 w-2/3 rounded bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200" />
                <div className="h-2.5 w-1/4 rounded bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200" />
            </div>
        ))}
    </div>
);

const FilterLocation = ({
    filter,
    state,
    updateFilter,
    currentLocationDisabled,
    currentLocation,
    getCurrentLocation,
    loading,
}: FilterLocationProps) => {
    const [searchParams] = useSearchParams();
    const { code: countryCode } = useCountryContext();
    const { filterKey } = filter;
    const requiredUseCurrentLocation = useRef(false);
    // Set when the user presses Enter before suggestions have loaded; the top
    // result is applied as soon as it arrives so Enter never does nothing.
    const pendingEnterSubmit = useRef(false);
    const [searchQuery, setSearchQuery] = useState('');
    const { selectedLocation } = state;
    const {
        changeKeyword: findPlace,
        flushKeyword,
        searchFilterOptions,
        optionsKeyword,
        loading: placesLoading,
    } = usePlaceFinder({
        type: FindPlaceTypeEnum.SearchFilter,
        limit: 10,
        apiProvider: FindPlaceApiProviderEnum.ServiceableArea,
    });

    const applyLocation = useCallback(
        (place?: BasicPlace) => {
            updateFilter(filterKey, {
                kind: 'Location',
                selectedLocation: place,
            });
        },
        [filterKey, updateFilter],
    );

    useEffect(() => {
        if (loading) return;
        if (currentLocation && requiredUseCurrentLocation.current) {
            setSearchQuery(formatLocationSearchFilterText(currentLocation));
            applyLocation(currentLocation);
        }
        requiredUseCurrentLocation.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLocation, filterKey, loading]);

    const trimmedSearchQuery = searchQuery.trim();
    const areSuggestionsFresh =
        trimmedSearchQuery.length >= MIN_PLACE_FINDER_KEYWORD_LENGTH &&
        trimmedSearchQuery === optionsKeyword;

    // Apply the top suggestion once results arrive after an early Enter press.
    useEffect(() => {
        if (!pendingEnterSubmit.current || placesLoading) return;
        if (!areSuggestionsFresh) return;
        pendingEnterSubmit.current = false;
        const topOption = searchFilterOptions[0];
        if (!topOption) return;
        setSearchQuery(topOption.header);
        applyLocation(topOption.value);
    }, [
        placesLoading,
        searchFilterOptions,
        applyLocation,
        areSuggestionsFresh,
    ]);

    const handleSearchKeyDown = useCallback(
        // The dropdown types this callback as a generic synthetic event, but it
        // only fires for keyboard events on the search input.
        (event: React.SyntheticEvent<HTMLElement>) => {
            if ((event.nativeEvent as KeyboardEvent).key !== 'Enter') return;
            if (trimmedSearchQuery.length < MIN_PLACE_FINDER_KEYWORD_LENGTH) {
                return;
            }
            // When suggestions match the current query the dropdown applies
            // the highlighted item on Enter. Stale suggestions from a prior
            // query stay visible during loading, so wait for fresh results.
            if (searchFilterOptions.length > 0 && areSuggestionsFresh) return;
            pendingEnterSubmit.current = true;
            // Fire any pending debounced search immediately.
            flushKeyword();
        },
        [
            trimmedSearchQuery,
            searchFilterOptions,
            areSuggestionsFresh,
            flushKeyword,
        ],
    );

    const handleUseCurrentLocation = useCallback(() => {
        if (currentLocation) {
            // don't update the filter state if it's the same location
            // it will cause updateFilter change
            if (
                currentLocation.suburb === selectedLocation?.suburb &&
                currentLocation.district === selectedLocation?.district &&
                currentLocation.city === selectedLocation?.city &&
                currentLocation.region === selectedLocation?.region &&
                currentLocation.state === selectedLocation?.state &&
                currentLocation.country === selectedLocation?.country
            ) {
                return;
            }
            setSearchQuery(formatLocationSearchFilterText(currentLocation));
            applyLocation(currentLocation);
        } else {
            if (!requiredUseCurrentLocation.current) {
                requiredUseCurrentLocation.current = true;
                getCurrentLocation();
            }
        }
    }, [currentLocation, getCurrentLocation, applyLocation, selectedLocation]);

    const useCurrentLocation = searchParams.get('currentLocation') === '1';

    useEffect(() => {
        if (!useCurrentLocation) return;
        handleUseCurrentLocation();
    }, [useCurrentLocation, handleUseCurrentLocation]);

    const isGeolocating = loading && requiredUseCurrentLocation.current;
    const showSkeleton = placesLoading && searchFilterOptions.length === 0;

    return (
        <div className="mb-4 flex flex-col md:mb-0">
            <SearchableDropdown
                items={searchFilterOptions.map((option, i) => ({
                    ...option,
                    className: '[&_.ui-box]:text-4',
                    content: (
                        <div className="mt-0.5 text-3.5 text-gray-500">
                            {option.placeType}
                        </div>
                    ),
                    key: option.header + i,
                }))}
                searchQuery={searchQuery}
                clearable
                // Highlight the top suggestion so Enter applies it without
                // needing to arrow down first.
                defaultHighlightedIndex={0}
                highlightFirstItemOnOpen
                loading={showSkeleton}
                loadingMessage={{
                    content: <LocationSuggestionsSkeleton />,
                }}
                noResultsMessage={
                    <span className="text-3.5 text-gray-500">
                        No matching locations — try a nearby city or region.
                    </span>
                }
                onSearchQueryChange={(_, data) => {
                    // Typing again invalidates a previous Enter press.
                    pendingEnterSubmit.current = false;
                    setSearchQuery(data.searchQuery ?? '');
                    findPlace(data.searchQuery);
                }}
                searchInput={{
                    placeholder: `City or ${(
                        getRegionNameByCountry(countryCode) || 'Region'
                    ).toLowerCase()}`,
                    disabled: isGeolocating,
                    // The dropdown invokes this after its own key handling, so
                    // Enter falls through here when no suggestion was applied.
                    onInputKeyDown: handleSearchKeyDown,
                }}
                variables={{
                    // Fix input left padding when item is selected on mobile.
                    searchInputPaddingMobile: selectedLocation
                        ? pxArrayToRem([11, 48, 11, 12])
                        : pxArrayToRem([11, 48]),
                }}
                onChange={(_, data) => {
                    pendingEnterSubmit.current = false;
                    let selectedPlace: DropdownItem<BasicPlace> | null = null;
                    if (data.value) {
                        // Ignore Enter/click on suggestions from a prior query.
                        if (!areSuggestionsFresh) return;
                        selectedPlace = data.value as DropdownItem<BasicPlace>;
                    } else {
                        requiredUseCurrentLocation.current = false;
                    }
                    applyLocation(selectedPlace?.value);
                }}
                toggleIndicator={
                    <span className={isGeolocating ? 'animate-pulse' : ''}>
                        <SearchLine size="2x" />
                    </span>
                }
            />
            <button
                className="mt-4 flex items-center gap-1 self-start text-4 text-gray-700 hover:underline md:text-5"
                onClick={handleUseCurrentLocation}
            >
                <CustomLocation className="size-4 md:size-5" />
                <span>Use my current location</span>
            </button>
            {currentLocationDisabled && (
                <div className="mt-4 text-3.5 leading-tight text-[#d03b47] md:text-4">
                    Please change your browser settings to allow location
                    services.
                </div>
            )}
        </div>
    );
};

export default FilterLocation;
